import { ContextUserType, Separator, Button, Avatar, IUser } from '@ui/components';
import dayjs from 'dayjs';
import Link from 'next/link';
import { twMerge } from 'tailwind-merge';
import { useRouter } from 'next/navigation';

interface CommunitySidebarProps {
  userType: ContextUserType;
  communitySet: any;
  isCommunitySuccess?: boolean;
  isLoadingCommunity?: boolean;
}

export const CommunitySidebar = ({
  userType,
  communitySet,
  isCommunitySuccess,
  isLoadingCommunity,
}: CommunitySidebarProps) => {
  const router = useRouter();
  const community = communitySet?.conversation;
  const restrictions = communitySet?.restrictions_list;
  const moderatorsProfiles = (restrictions?.moderators_profiles || []) as IUser[];
  const displayedModerators = moderatorsProfiles.slice(0, 6);

  const onViewAllModerators = () => {
    if (userType === 'creator') {
      router.push('/dashboard/community/moderators');
    } else {
      router.push(`/creator/community/moderators?creatorId=${community.creator_id}`);
    }
  };

  if (!community) return null;

  return (
    <div className="w-[300px] space-y-4">
      <div className="rounded border border-gray-200 bg-white p-4 space-y-4">
        <div>
          <h2 className="text-md font-semibold">{community.name}</h2>
          <div className="space-y-2">
            <p className="text-sm">{community.description}</p>
            <div className="flex items-center gap-1 text-gray-500">
              <h3 className="text-xs ">Created</h3>
              <p className="text-xs">{dayjs(community.created_at).format('MMM D, YYYY')}</p>
            </div>
          </div>
        </div>

        <Link
          href={
            userType === 'creator' ? '/dashboard/community-guidelines' : '/community-guidelines'
          }
          className={twMerge(
            'text-xs underline hover:text-primary',
            userType === 'fan' ? 'px-4 lg:px-0' : '',
          )}>
          Community Guidelines
        </Link>

        <Separator />

        <div>
          <h2 className="text-md font-semibold mb-2">Moderators</h2>
          {moderatorsProfiles?.length > 0 ? (
            <div className="space-y-2">
              {displayedModerators.map((moderator) => (
                <div
                  key={moderator.id}
                  className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                  onClick={() =>
                    router.push(
                      `/fan-profile/?fanId=${moderator.id}&creatorId=${community.creator_id}`,
                    )
                  }>
                  <Avatar size="s" src={moderator.avatarURL} type="fan" />
                  <span className="text-sm">{moderator.userName}</span>
                </div>
              ))}

              <Button
                type="secondary"
                size="small"
                className="w-full mt-2"
                onClick={onViewAllModerators}>
                View all
              </Button>
            </div>
          ) : (
            userType === 'creator' && (
              <div className="py-4 space-y-3">
                <p className="text-sm text-gray-500">This community currently has no moderators.</p>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => router.push('/dashboard/community/my-fans')}>
                  Add Moderators
                </Button>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};
